import axios from "axios";
import { useState, useRef, useEffect } from "react";

import Input from '@/components/Input';
import Button from '@/components/Button';
import { getErrorMessage, checkPasswordConditions } from '@/utils/Errors';
import { nowByType } from '@/utils/Dates';
import Dropdown from '@/components/Dropdown';

export default function CreateUser({t,language,user,apiUrl,token,sendMessage,setRootAnswer,handleLogin}){

    const divRef = useRef(null); 

    const [ data, setData ] = useState({
        name: '',
        email: '',
        password: '',
        roles: [],
        companies: [],
        // default values that cannot be null
        type: 9,
        module: '{7}',
        created: nowByType('datetime'),
        legal: 0,
        reset: 1,
        active: 1
    });

    const [ errors, setErrors ] = useState(null);

    const [ roles, setRoles ] = useState([]);
    const getRoles = () => {
        
        var apiPath = '/roles/list';

        const config = {
            headers: { Authorization: `Bearer ${token}` },   
            // params is the same as using ?database=, query params      
            params: {
                language: language,
                companyuser: user?.company
            }  
        };

        var errorMessage = t('root.create_user.errors.roles');
        
        axios.get(apiUrl+apiPath,config)
        .then(function (response) {

            if(response?.data){
          
                const responseData = response.data;                

                // if result is ok (1)
                if(responseData?.result){                                        
                    
                    const data = responseData?.data

                    if(data?.items){                        

                        // get roles id,name for the dropdown
                        const rolesInfo = [];
                        data.items?.forEach((item) => {
                            rolesInfo.push({id:item?.id,name:item?.nom});
                        });
                        setRoles(rolesInfo);
                    }
                    else{
                        setRoles([]);
                    }
                } 
                else{
                    if(responseData?.status && responseData.status === 401){
                        handleLogin();                        
                    }
                    else{
                        // check error message
                        if(responseData?.data?.reasons){
                            errorMessage = getErrorMessage(t,'root.create_user',responseData?.data?.reasons);                                
                        }
                        
                        sendMessage(errorMessage,'error');
                    }                        
                }
            }
            else{                
                sendMessage(errorMessage,'error');                    
            }
        })
        .catch(function (error){
            
            if(error?.status && error.status === 401){
                handleLogin();                        
            }
            else{                        
                sendMessage(errorMessage,'error');
            }
        });
    }

    const [ companies, setCompanies ] = useState([]);
    const getCompanies = () => {
        
        var apiPath = '/companies/list';

        const config = {
            headers: { Authorization: `Bearer ${token}` },   
            // params is the same as using ?database=, query params      
            params: {
                language: language,
                companyuser: user?.company
            }  
        };

        var errorMessage = t('root.create_user.errors.companies');
        
        axios.get(apiUrl+apiPath,config)
        .then(function (response) {

            if(response?.data){
          
                const responseData = response.data;                

                // if result is ok (1)
                if(responseData?.result){                                        
                    
                    const data = responseData?.data

                    if(data?.items){                        

                        // get roles id,name for the dropdown
                        const companiesInfo = [];
                        data.items?.forEach((item) => {
                            companiesInfo.push({id:item?.id,name:item?.empresa_nom});
                        });
                        setCompanies(companiesInfo);
                    }
                    else{
                        setCompanies([]);
                    }
                } 
                else{
                    if(responseData?.status && responseData.status === 401){
                        handleLogin();                        
                    }
                    else{
                        // check error message
                        if(responseData?.data?.reasons){
                            errorMessage = getErrorMessage(t,'root.create_user',responseData?.data?.reasons);                                
                        }
                        
                        sendMessage(errorMessage,'error');
                    }                        
                }
            }
            else{                
                sendMessage(errorMessage,'error');                    
            }
        })
        .catch(function (error){
            
            if(error?.status && error.status === 401){
                handleLogin();                        
            }
            else{                        
                sendMessage(errorMessage,'error');
            }
        });
    }

    useEffect(() => {
        // scroll to div
        if(divRef.current) {
            divRef.current.scrollIntoView({ 
                behavior: 'smooth',
                block: 'start'
            });
        }

        // get the list of roles
        getRoles();

        // get the list of companies
        getCompanies();
        
    }, []);    

    const handleInput = (e) => {
        
        var name = e.target.name;
        var value = e.target.value;

        var newData = {...data};
        newData[name] = value;
        setData(newData);
    };

    // manage roles    
    const handleRoles = (dropdownData) => {       
        var newData = {...data};
        newData['roles'] = dropdownData;
        setData(newData);
    }

    // manage companies    
    const handleCompanies = (dropdownData) => {       
        var newData = {...data};
        newData['companies'] = dropdownData;
        setData(newData);
    }

    const handleSubmit = () => {
        
        var errorsCheck = {};

        if(data.name.length === 0){
            errorsCheck.name = 'empty';
        }

        if(data.email.length === 0){
            errorsCheck.email = 'empty';
        }

        if(data.password.length === 0){
            errorsCheck.password = 'empty';
        }

        // check password conditions
        const errorsPassword = checkPasswordConditions(data.password);
        const keysPassword = Object.keys(errorsPassword);               
        if(keysPassword && keysPassword?.length > 0){
            errorsCheck = {...errorsCheck,...errorsPassword}
        }

        if(Object.keys(errorsCheck).length === 0){

            setErrors(null);
            
            const apiPath = '/users/set';

            const config = {
                headers: { Authorization: `Bearer ${token}` } 
            };

            const send = {...data};

            send.language = language;
            send.companyuser = user?.company;

            // change roles to csv values
            if(data?.roles){
                send.roles = data?.roles?.map(role => role.id)?.join(',');
            }

            // change companies to csv values
            if(data?.companies){
                send.companies = data?.companies?.map(company => company.id)?.join(',');
            }

            var errorMessage = t('root.create_user.errors.create');
            
            const url = apiUrl+apiPath;
            
            axios.post(url,send,config)
            .then(function (response) {

                if(response?.data){
              
                    const responseData = response.data;                

                    // if result is ok (1)
                    if(responseData?.result){

                        sendMessage(t('root.create_user.created'));
                        
                        const dataItems = responseData?.data;

                        const render = 
                            <div className='mt-4'>                                       
                                <div className="text-xl font-bold mb-2">
                                    {t('root.create_user.created')}
                                </div>                        
                                <div className="">
                                    {
                                        dataItems?.item && Object.keys(dataItems?.item)?.map((key) => (
                                            <div key={'root-action-create-user-item-'+dataItems.item?.id+'-column-'+key}>
                                                <b>{key}</b>: {dataItems.item[key]}
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                        ;
                        setRootAnswer(render);
                    } 
                    else{
                        if(responseData?.status && responseData.status === 401){
                            handleLogin();                        
                        }
                        else{
                            // check error message
                            if(responseData?.data?.reasons){
                                errorMessage = getErrorMessage(t,'root.create_user',responseData?.data?.reasons);   
                                setErrors(responseData?.data?.reasons);                             
                            }
                            
                            sendMessage(errorMessage,'error');
                        }                        
                    }
                }
                else{                
                    sendMessage(errorMessage,'error');                    
                }
            })
            .catch(function (error){
                
                if(error?.status && error.status === 401){
                    handleLogin();                        
                }
                else{                        
                    sendMessage(errorMessage,'error');
                }
            });
        }
        else{
            setErrors(errorsCheck);
            const errorMessage = getErrorMessage(t,'root.create_user',errorsCheck);
            sendMessage(errorMessage,'error');
        }
    }

	return (
        <div className='mt-4' ref={divRef}>
            <div className='mb-4'>
                {t('root.create_user.description')}
            </div>
            <div className='mb-4'>
                <Input                      
                    name="name"
                    type="text"
                    value={data.name}                      
                    placeholder={t('root.create_user.fields.name')}                    
                    onChange={handleInput}
                    error={errors && errors?.name ? t('root.create_user.errors.'+errors.name) : ''}
                    //handleKeyDown={}
                />
            </div>
            <div className='mb-4'>
                <Input                      
                    name="email"
                    type="email"
                    value={data.email}                      
                    placeholder={t('root.create_user.fields.email')}                    
                    onChange={handleInput}
                    error={errors && errors?.email ? t('root.create_user.errors.'+errors.email) : ''}
                    //handleKeyDown={}
                />
            </div>
            <div className='mb-4'>
                <Input                      
                    name="password"
                    type="text"
                    value={data.password}                      
                    placeholder={t('root.create_user.fields.password')}                    
                    onChange={handleInput}
                    error={errors && errors?.password ? t('root.create_user.errors.'+errors.password) : ''}
                    //handleKeyDown={}
                />
            </div>
            <div className='mb-4'>
                <Dropdown                    
                    type='multiple'
                    name='roles'
                    value={data.roles}
                    options={roles}
                    handleDropdown={handleRoles}
                    //error={}
                />
            </div>
            <div className='mb-4'>
                <Dropdown                    
                    type='multiple'
                    name='companies'
                    value={data.companies}
                    options={companies}
                    handleDropdown={handleCompanies}
                    //error={}
                />
            </div>
            <div className='text-center'>
                <Button                     
                    onClick={handleSubmit}
                    text={t('root.create_user.button')}
                    type='accept'            
                />
            </div>
        </div>
    );
}